import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { fmtMoney } from '../../utils/formatters'
import { SHEET_TYPES, sheetTypeByKey, visibleQuestions, missingRequired, buildLines, sheetTotal, jobNameFor } from '../../lib/bidSheet'

// Pool bid sheet: pick the job type, answer that type's questions, and the
// bid lines fill in from the pricing tables. Inground vinyl installs hand off
// to the existing IngroundCalculator via onPickInground.
export default function PoolBidSheet({ bid, initialType, pricing, onApply, onPickInground, onClose }) {
  var saved = (bid && bid.calc_inputs && bid.calc_inputs.bid_sheet) || null
  const [type, setType] = useState(initialType || (saved && saved.type) || null)
  const [answers, setAnswers] = useState((saved && saved.type === (initialType || saved.type) && saved.answers) || {})
  const [ctxData, setCtxData] = useState({ sand: [], addons: [], models: [], config: {}, igLiners: [] })
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [showNewModel, setShowNewModel] = useState(false)
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 760)

  useEffect(function() {
    var onResize = function() { setNarrow(window.innerWidth < 760) }
    window.addEventListener('resize', onResize)
    return function() { window.removeEventListener('resize', onResize) }
  }, [])

  useEffect(function() {
    var alive = true
    Promise.all([
      supabase.from('pool_sand_standards').select('*'),
      supabase.from('pool_calc_addons').select('*').eq('active', true).order('sort_order'),
      supabase.from('fiberglass_pool_models').select('*').eq('active', true).order('manufacturer').order('model_name'),
      supabase.from('pool_calc_config').select('*').limit(1).maybeSingle(),
      supabase.from('pool_pricing').select('*').eq('category', 'inground_liner').eq('is_active', true).order('shape').order('size'),
    ]).then(function(res) {
      if (!alive) return
      setCtxData({ sand: res[0].data ?? [], addons: res[1].data ?? [], models: res[2].data ?? [], config: res[3].data ?? {}, igLiners: (res[4].data ?? []).map(function(r) { return { ...r, id: String(r.id) } }) })
      setLoading(false)
    })
    return function() { alive = false }
  }, [])

  var ctx = useMemo(function() { return { pricing: pricing || [], ...ctxData } }, [pricing, ctxData])
  var lines = useMemo(function() { return type ? buildLines(type, answers, ctx) : [] }, [type, answers, ctx])
  var total = sheetTotal(lines)
  var unpriced = lines.filter(function(l) { return l.unpriced })
  var missing = type ? missingRequired(type, ctx, answers) : []

  var set = function(key, val) { setAnswers(function(prev) { return { ...prev, [key]: val } }) }

  var pickType = function(key) {
    if (key === 'ig_install') { onPickInground(); return }
    if (key !== type) setAnswers({})
    setType(key)
  }

  var apply = async function() {
    if (missing.length || !lines.length) return
    setApplying(true)
    var t = sheetTypeByKey(type)
    await onApply({ type: type, bidType: t.bidType, answers: answers, lines: lines, jobName: jobNameFor(type, answers, ctx) })
    setApplying(false)
  }

  var addModel = async function(m) {
    var { data, error } = await supabase.from('fiberglass_pool_models').insert(m).select().single()
    if (error) { alert('Could not save model: ' + error.message); return }
    setCtxData(function(prev) { return { ...prev, models: prev.models.concat([data]) } })
    set('model_id', data.id)
    setShowNewModel(false)
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={function(e) { e.stopPropagation() }}>
        <div style={s.header}>
          <div style={s.title}>{type ? 'BID SHEET — ' + sheetTypeByKey(type).label.toUpperCase() : 'NEW POOL BID — PICK THE JOB TYPE'}</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {type && <button onClick={function() { setType(null) }} style={s.ghostBtn}>CHANGE TYPE</button>}
            <button onClick={onClose} style={s.ghostBtn}>X</button>
          </div>
        </div>

        {!type && (
          <div style={s.typeGrid}>
            {SHEET_TYPES.map(function(t) {
              return (
                <button key={t.key} onClick={function() { pickType(t.key) }} style={s.typeTile}>
                  <div style={s.typeLabel}>{t.label.toUpperCase()}</div>
                  <div style={s.typeBlurb}>{t.blurb}</div>
                </button>
              )
            })}
          </div>
        )}

        {type && loading && <div style={s.muted}>Loading pricing…</div>}

        {type && !loading && (
          <div style={{ ...s.body, ...(narrow ? { gridTemplateColumns: '1fr' } : {}) }}>
            <div style={s.questions}>
              {visibleQuestions(type, ctx, answers).map(function(q) {
                return (
                  <div key={q.key} style={s.q}>
                    <div style={s.qLabel}>{q.label.toUpperCase()}{q.required ? ' *' : ''}</div>
                    <Field q={q} value={answers[q.key]} answers={answers} ctx={ctx} onChange={function(v) { set(q.key, v) }} onNewModel={function() { setShowNewModel(true) }} />
                  </div>
                )
              })}
              {showNewModel && <NewModelForm onSave={addModel} onCancel={function() { setShowNewModel(false) }} />}
            </div>

            <div style={s.preview}>
              <div style={s.qLabel}>BID LINES</div>
              {lines.length === 0 && <div style={s.muted}>{missing.length ? 'Answer: ' + missing.join(', ') : 'Answer the questions to build the bid.'}</div>}
              {lines.map(function(l, i) {
                return (
                  <div key={i} style={{ ...s.lineRow, ...(l.note ? s.noteRow : {}) }}>
                    <div style={{ flex: 1 }}>
                      <div style={s.lineDesc}>{l.description}</div>
                      {!l.note && l.quantity !== 1 && <div style={s.lineSub}>{l.quantity} {l.unit} × {fmtMoney(l.unit_cost)}</div>}
                    </div>
                    {!l.note && <div style={l.unpriced ? s.unpriced : s.lineAmt}>{l.unpriced ? 'SET PRICE' : fmtMoney(l.total_cost)}</div>}
                  </div>
                )
              })}
              {lines.length > 0 && <div style={s.totalRow}><span>TOTAL</span><span>{fmtMoney(total)}</span></div>}
              {unpriced.length > 0 && <div style={s.warn}>{unpriced.length} line{unpriced.length > 1 ? 's have' : ' has'} no price yet. Set it on the Pricing page, or edit the line on the bid after applying.</div>}
              {saved && saved.type === type && <div style={s.muted}>Applying replaces the lines this sheet added last time. Lines you added by hand stay.</div>}
              <button onClick={apply} disabled={applying || missing.length > 0 || lines.length === 0} style={{ ...s.applyBtn, ...((applying || missing.length > 0 || lines.length === 0) ? s.applyOff : {}) }}>
                {applying ? 'APPLYING…' : 'APPLY TO BID'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ q, value, ctx, onChange, onNewModel }) {
  if (q.type === 'choice') {
    return (
      <div style={s.choiceRow}>
        {q.options.map(function(o) {
          var on = value === o[0]
          return <button key={o[0]} onClick={function() { onChange(on ? null : o[0]) }} style={{ ...s.choice, ...(on ? s.choiceOn : {}) }}>{o[1]}</button>
        })}
      </div>
    )
  }
  if (q.type === 'yesno') {
    return (
      <div style={s.choiceRow}>
        {[[true, 'Yes'], [false, 'No']].map(function(o) {
          var on = value === o[0]
          return <button key={String(o[0])} onClick={function() { onChange(on ? null : o[0]) }} style={{ ...s.choice, ...(on ? s.choiceOn : {}) }}>{o[1]}</button>
        })}
      </div>
    )
  }
  if (q.type === 'multi') {
    var arr = value || []
    return (
      <div style={s.choiceRow}>
        {q.options.map(function(o) {
          var on = arr.indexOf(o[0]) >= 0
          return <button key={o[0]} onClick={function() { onChange(on ? arr.filter(function(x) { return x !== o[0] }) : arr.concat([o[0]])) }} style={{ ...s.choice, ...(on ? s.choiceOn : {}) }}>{on ? '✓ ' : ''}{o[1]}</button>
        })}
      </div>
    )
  }
  if (q.type === 'select') {
    return (
      <select value={value ?? ''} onChange={function(e) { onChange(e.target.value || null) }} style={s.input}>
        <option value="">Select…</option>
        {q.options.map(function(o) { return <option key={o[0]} value={o[0]}>{o[1]}</option> })}
      </select>
    )
  }
  if (q.type === 'number') {
    var model = q.placeholderFrom ? null : undefined
    return <input type="number" inputMode="decimal" value={value ?? ''} placeholder={model === null ? 'Model default' : '0'} onChange={function(e) { onChange(e.target.value === '' ? null : e.target.value) }} style={s.input} />
  }
  if (q.type === 'text') {
    return <input value={value ?? ''} onChange={function(e) { onChange(e.target.value) }} style={s.input} />
  }
  if (q.type === 'model') {
    return (
      <div style={{ display: 'flex', gap: '8px' }}>
        <select value={value ?? ''} onChange={function(e) { onChange(e.target.value || null) }} style={{ ...s.input, flex: 1 }}>
          <option value="">Select model…</option>
          {(ctx.models || []).map(function(m) {
            return <option key={m.id} value={m.id}>{(m.manufacturer ? m.manufacturer + ' — ' : '') + m.model_name + ' (' + m.width_ft + 'x' + m.length_ft + ', ' + m.depth_shallow_ft + "'–" + m.depth_deep_ft + "')"}</option>
          })}
        </select>
        <button onClick={onNewModel} style={s.ghostBtn}>+ NEW MODEL</button>
      </div>
    )
  }
  if (q.type === 'addons') {
    var picked = value || {}
    return (
      <div style={s.addonGrid}>
        {(ctx.addons || []).map(function(ad) {
          var v = picked[ad.id]
          var setA = function(nv) { var next = { ...picked }; if (nv == null || nv === false || nv === '') delete next[ad.id]; else next[ad.id] = nv; onChange(next) }
          if (ad.pricing_type === 'flat') {
            return <button key={ad.id} onClick={function() { setA(v ? null : true) }} style={{ ...s.choice, ...(v ? s.choiceOn : {}), textAlign: 'left' }}>{v ? '✓ ' : ''}{ad.name} · {fmtMoney(ad.customer_price)}</button>
          }
          return (
            <div key={ad.id} style={s.addonQty}>
              <span style={{ flex: 1 }}>{ad.name} · {fmtMoney(ad.customer_price)}/{ad.unit_label}</span>
              <input type="number" inputMode="decimal" value={v ?? ''} placeholder={ad.unit_label} onChange={function(e) { setA(e.target.value === '' ? null : e.target.value) }} style={{ ...s.input, width: '90px' }} />
            </div>
          )
        })}
      </div>
    )
  }
  return null
}

function NewModelForm({ onSave, onCancel }) {
  const [m, setM] = useState({ manufacturer: '', model_name: '', shape: 'Rectangle', width_ft: '', length_ft: '', depth_shallow_ft: '', depth_deep_ft: '', shell_cost: '', base_install_price: '' })
  var f = function(k, label, numeric) {
    return (
      <div>
        <div style={s.qLabel}>{label}</div>
        <input type={numeric ? 'number' : 'text'} value={m[k]} onChange={function(e) { setM({ ...m, [k]: e.target.value }) }} style={s.input} />
      </div>
    )
  }
  var save = function() {
    if (!m.model_name || !m.width_ft || !m.length_ft) { alert('Model name, width, and length are required.'); return }
    var row = { ...m, active: true }
    ;['width_ft', 'length_ft', 'depth_shallow_ft', 'depth_deep_ft', 'shell_cost', 'base_install_price'].forEach(function(k) { row[k] = row[k] === '' ? null : parseFloat(row[k]) })
    if (!row.manufacturer) row.manufacturer = null
    onSave(row)
  }
  return (
    <div style={s.newModel}>
      <div style={s.qLabel}>NEW FIBERGLASS MODEL</div>
      <div style={s.newModelGrid}>
        {f('manufacturer', 'MANUFACTURER')}{f('model_name', 'MODEL NAME *')}{f('shape', 'SHAPE')}
        {f('width_ft', 'WIDTH (FT) *', true)}{f('length_ft', 'LENGTH (FT) *', true)}{f('depth_shallow_ft', 'SHALLOW (FT)', true)}
        {f('depth_deep_ft', 'DEEP (FT)', true)}{f('shell_cost', 'SHELL COST ($)', true)}{f('base_install_price', 'INSTALL PRICE ($)', true)}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button onClick={save} style={s.applyBtnSm}>SAVE MODEL</button>
        <button onClick={onCancel} style={s.ghostBtn}>CANCEL</button>
      </div>
    </div>
  )
}

var GOLD = '#b48c3c'
var s = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '24px', width: '100%', maxWidth: '1040px', maxHeight: '92vh', overflow: 'auto', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '20px', fontWeight: 700, letterSpacing: '3px', color: GOLD },
  ghostBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', padding: '8px 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' },
  typeTile: { textAlign: 'left', padding: '20px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', cursor: 'pointer', color: '#fff' },
  typeLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#fff', marginBottom: '6px' },
  typeBlurb: { fontSize: '13px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 },
  body: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: '24px' },
  questions: { display: 'flex', flexDirection: 'column', gap: '14px' },
  q: {},
  qLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' },
  choiceRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  choice: { padding: '9px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: 'rgba(255,255,255,0.6)', fontSize: '13px', cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  choiceOn: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.5)', color: GOLD, fontWeight: 600 },
  input: { width: '100%', padding: '9px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: '#fff', fontSize: '14px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  addonGrid: { display: 'flex', flexDirection: 'column', gap: '6px' },
  addonQty: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' },
  preview: { position: 'sticky', top: 0, zIndex: 1, alignSelf: 'start', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', padding: '16px' },
  lineRow: { display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  noteRow: { opacity: 0.6 },
  lineDesc: { fontSize: '13px', color: '#fff' },
  lineSub: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
  lineAmt: { fontSize: '13px', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' },
  unpriced: { fontSize: '10px', letterSpacing: '1px', color: '#e0a84c', fontWeight: 700, whiteSpace: 'nowrap', fontFamily: "'Barlow Condensed', sans-serif" },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: GOLD },
  warn: { fontSize: '12px', color: '#e0a84c', margin: '8px 0', lineHeight: 1.4 },
  muted: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', margin: '8px 0', lineHeight: 1.4 },
  applyBtn: { width: '100%', marginTop: '12px', padding: '12px', backgroundColor: GOLD, border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  applyBtnSm: { padding: '8px 16px', backgroundColor: GOLD, border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  applyOff: { opacity: 0.4, cursor: 'not-allowed' },
  newModel: { border: '1px solid rgba(180,140,60,0.3)', backgroundColor: 'rgba(180,140,60,0.05)', borderRadius: '4px', padding: '14px' },
  newModelGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' },
}
